export type LetterState = "correct" | "present" | "absent";

export type GameStatus = "playing" | "won" | "lost";

export type DailyLevel = "easy" | "classic" | "expert";

export interface EvaluatedLetter {
  letter: string;
  state: LetterState;
}

export interface GameState {
  dateKey: string;
  answer: string;
  guesses: string[];
  currentGuess: string;
  status: GameStatus;
  hardMode: boolean;
  mode: "daily" | "unlimited";
  level: DailyLevel;
  startedAt: number;
  completedAt?: number;
  hintsUsed: number;
  evaluations?: LetterState[][];
  hint?: string;
}

export interface Stats {
  played: number;
  wins: number;
  currentStreak: number;
  maxStreak: number;
  lastCompletedDate?: string;
  lastStreakDate?: string;
  completedDailyKeys: string[];
  distribution: number[];
  nexus: number;
  ownedPerks: string[];
  activeTheme: string;
  hints: number;
  lastBonusDate?: string;
  shareRewardDates: string[];
  profileName: string;
  avatarColor: string;
  purchaseHistory: PurchaseRecord[];
  adRewardClaims: Record<string, AdRewardClaims>;
  streakShields: number;
  dailyGoalClaims: string[];
  unlimitedLives: number;
  unlimitedLifeUpdatedAt?: number;
  unlimitedWins: number;
  unlimitedCurrentRun: number;
  unlimitedBestRun: number;
}

export interface PurchaseRecord {
  productId: string;
  transactionId: string;
  platform: string;
  purchasedAt: number;
}

export interface AdRewardClaims {
  hint: number;
  nexus: number;
}

export const EMPTY_STATS: Stats = {
  played: 0,
  wins: 0,
  currentStreak: 0,
  maxStreak: 0,
  distribution: [0, 0, 0, 0, 0, 0, 0],
  completedDailyKeys: [],
  nexus: 60,
  ownedPerks: ["theme-sunset"],
  activeTheme: "theme-sunset",
  hints: 1,
  shareRewardDates: [],
  profileName: "Player",
  avatarColor: "#fbbf24",
  purchaseHistory: [],
  adRewardClaims: {},
  streakShields: 0,
  dailyGoalClaims: [],
  unlimitedLives: 1,
  unlimitedWins: 0,
  unlimitedCurrentRun: 0,
  unlimitedBestRun: 0
};

function getDateGap(previousDate: string | undefined, currentDate: string) {
  if (!previousDate) return 0;
  const previous = Date.parse(`${previousDate}T00:00:00Z`);
  const current = Date.parse(`${currentDate}T00:00:00Z`);
  if (Number.isNaN(previous) || Number.isNaN(current)) return 1;
  return Math.round((current - previous) / 86400000);
}

export function getMaxGuesses(level: DailyLevel = "classic") {
  if (level === "easy") return 7;
  if (level === "expert") return 5;
  return 6;
}

export function getDailyPlayKey(game: Pick<GameState, "dateKey" | "level" | "mode">) {
  return game.mode === "daily" ? `${game.dateKey}:${game.level}` : game.dateKey;
}

export function createGame(
  dateKey: string,
  answer: string,
  mode: GameState["mode"] = "daily",
  level: DailyLevel = "classic"
): GameState {
  return {
    dateKey,
    answer,
    guesses: [],
    currentGuess: "",
    status: "playing",
    hardMode: false,
    mode,
    level,
    startedAt: Date.now(),
    hintsUsed: 0
  };
}

export function getElapsedSeconds(game: GameState) {
  if (!game.startedAt) return 1;
  const end = game.completedAt ?? Date.now();
  return Math.max(1, Math.round((end - game.startedAt) / 1000));
}

export function getDailyScore(game: GameState, streak: number) {
  if (game.status !== "won") return 0;
  const guessPenalty = Math.max(0, game.guesses.length - 1) * 120;
  const hintPenalty = (game.hintsUsed ?? 0) * 150;
  const timePenalty = Math.min(300, getElapsedSeconds(game));
  const streakBonus = Math.min(streak * 10, 100);
  return Math.max(100, 1000 - guessPenalty - hintPenalty - timePenalty + streakBonus);
}

export function evaluateGuess(guess: string, answer: string): EvaluatedLetter[] {
  const result: EvaluatedLetter[] = guess.split("").map((letter) => ({ letter, state: "absent" }));
  const remaining = answer.split("");

  for (let index = 0; index < guess.length; index += 1) {
    if (guess[index] === answer[index]) {
      result[index].state = "correct";
      remaining[index] = "";
    }
  }

  for (let index = 0; index < guess.length; index += 1) {
    if (result[index].state === "correct") {
      continue;
    }

    const matchIndex = remaining.indexOf(guess[index]);
    if (matchIndex >= 0) {
      result[index].state = "present";
      remaining[matchIndex] = "";
    }
  }

  return result;
}

export function getKeyboardStates(guesses: string[], answer: string) {
  const rank: Record<LetterState, number> = { absent: 1, present: 2, correct: 3 };
  const states = new Map<string, LetterState>();

  for (const guess of guesses) {
    for (const item of evaluateGuess(guess, answer)) {
      const previous = states.get(item.letter);
      if (!previous || rank[item.state] > rank[previous]) {
        states.set(item.letter, item.state);
      }
    }
  }

  return states;
}

export function getKeyboardStatesFromEvaluations(guesses: string[], evaluations: LetterState[][]) {
  const rank: Record<LetterState, number> = { absent: 1, present: 2, correct: 3 };
  const states = new Map<string, LetterState>();

  guesses.forEach((guess, rowIndex) => {
    guess.split("").forEach((letter, letterIndex) => {
      const state = evaluations[rowIndex]?.[letterIndex];
      if (!state) return;
      const previous = states.get(letter);
      if (!previous || rank[state] > rank[previous]) {
        states.set(letter, state);
      }
    });
  });

  return states;
}

export function updateStats(stats: Stats, game: GameState): Stats {
  const normalized: Stats = {
    ...EMPTY_STATS,
    ...stats,
    distribution: stats.distribution ?? EMPTY_STATS.distribution,
    completedDailyKeys: stats.completedDailyKeys ?? EMPTY_STATS.completedDailyKeys,
    ownedPerks: stats.ownedPerks ?? EMPTY_STATS.ownedPerks,
    shareRewardDates: stats.shareRewardDates ?? EMPTY_STATS.shareRewardDates,
    profileName: stats.profileName ?? EMPTY_STATS.profileName,
    avatarColor: stats.avatarColor ?? EMPTY_STATS.avatarColor,
    purchaseHistory: stats.purchaseHistory ?? EMPTY_STATS.purchaseHistory,
    adRewardClaims: stats.adRewardClaims ?? EMPTY_STATS.adRewardClaims,
    streakShields: stats.streakShields ?? EMPTY_STATS.streakShields,
    dailyGoalClaims: stats.dailyGoalClaims ?? EMPTY_STATS.dailyGoalClaims,
    unlimitedLives: stats.unlimitedLives ?? EMPTY_STATS.unlimitedLives,
    unlimitedLifeUpdatedAt: stats.unlimitedLifeUpdatedAt,
    unlimitedWins: stats.unlimitedWins ?? EMPTY_STATS.unlimitedWins,
    unlimitedCurrentRun: stats.unlimitedCurrentRun ?? EMPTY_STATS.unlimitedCurrentRun,
    unlimitedBestRun: stats.unlimitedBestRun ?? EMPTY_STATS.unlimitedBestRun
  };

  const playKey = getDailyPlayKey(game);
  if (game.mode !== "daily" || normalized.completedDailyKeys.includes(playKey) || game.status === "playing") {
    return normalized;
  }

  const won = game.status === "won";
  const firstWinForDate = won && normalized.lastStreakDate !== game.dateKey;
  const dateGap = firstWinForDate ? getDateGap(normalized.lastStreakDate, game.dateKey) : 0;
  const shieldMissedDay = firstWinForDate && dateGap === 2 && normalized.streakShields > 0;
  const nextStreak = firstWinForDate
    ? !normalized.lastStreakDate || dateGap <= 1 || shieldMissedDay
      ? normalized.currentStreak + 1
      : 1
    : normalized.currentStreak;
  const next: Stats = {
    ...normalized,
    played: normalized.played + 1,
    wins: normalized.wins + (won ? 1 : 0),
    currentStreak: nextStreak,
    maxStreak: normalized.maxStreak,
    lastCompletedDate: game.dateKey,
    lastStreakDate: firstWinForDate ? game.dateKey : normalized.lastStreakDate,
    completedDailyKeys: [...new Set([...normalized.completedDailyKeys, playKey])],
    distribution: [...normalized.distribution],
    nexus: normalized.nexus + (won ? 35 : 8),
    streakShields: shieldMissedDay ? Math.max(0, normalized.streakShields - 1) : normalized.streakShields
  };

  if (won) {
    next.maxStreak = Math.max(next.maxStreak, next.currentStreak);
    const distributionIndex = Math.max(0, game.guesses.length - 1);
    while (next.distribution.length <= distributionIndex) {
      next.distribution.push(0);
    }
    next.distribution[distributionIndex] = (next.distribution[distributionIndex] ?? 0) + 1;
  }

  return next;
}

export function getShareText(game: GameState, puzzleNumber: number) {
  const rows = game.guesses.map((guess) =>
    evaluateGuess(guess, game.answer)
      .map((item) => {
        if (item.state === "correct") return "🟩";
        if (item.state === "present") return "🟨";
        return "⬛";
      })
      .join("")
  );

  const maxGuesses = getMaxGuesses(game.level);
  const score = game.status === "won" ? `${game.guesses.length}/${maxGuesses}` : `X/${maxGuesses}`;
  const levelLabel = game.mode === "daily" ? ` ${game.level.toUpperCase()}` : " UNLIMITED";
  return `Lexi Link #${puzzleNumber}${levelLabel} ${score}\n${rows.join("\n")}`;
}
